/* =============================================================================
   guest.jsx  —  the public guest experience. Renders only sections whose
   toggle is on. Order: hero, details, registry, rsvp, wishes, predictions,
   gallery, faq, (schedule, virtual), footer.
============================================================================= */
import { useState } from 'react';
import { SectionHead, useAsyncData, Loading, ErrorBlock } from './ui';
import { Sprig, Wildflower, LeafSprig, Ladybug, Bee } from './decor';
import { RSVPForm, WishForm, PredictionForm } from './forms';
import { downloadICS, directionsUrl, normalizeUrl } from '../lib/helpers';
import { DB } from '../lib/api';

/* ------------------------------- top nav --------------------------------- */
function GuestNav({ toggles, hosts }) {
  const [open, setOpen] = useState(false);
  const links = [
    ['details', 'Details'],
    toggles.registry && ['registry', 'Registry'],
    ['rsvp', 'RSVP'],
    toggles.wishes && ['wishes', 'Well wishes'],
    toggles.predictions && ['predictions', 'Guesses'],
    toggles.faq && ['faq', 'FAQ'],
  ].filter(Boolean);
  return (
    <header className="gnav no-print">
      <div className="gnav-inner wrap">
        <a href="#top" className="gnav-brand"><Sprig size={20} /> {hosts}</a>
        <nav className={`gnav-links ${open ? 'open' : ''}`}>
          {links.map(([id, label]) => (
            <a key={id} href={`#${id}`} onClick={() => setOpen(false)}>{label}</a>
          ))}
          <a href="#rsvp" className="btn btn-primary btn-sm gnav-cta" onClick={() => setOpen(false)}>RSVP</a>
        </nav>
        <button className="gnav-burger" aria-label="Menu" onClick={() => setOpen((o) => !o)}>
          <span /><span /><span />
        </button>
      </div>
    </header>
  );
}

/* --------------------------------- hero ---------------------------------- */
// Video hero: the watercolor invitation animation is the full hero (it already
// contains the headline, hosts, and date). Below it sits only the RSVP call to
// action. A visually-hidden heading preserves the page title for screen readers
// and search engines. Drop the clip at frontend/public/hero.mp4.
function Hero({ event }) {
  return (
    <section id="top" className="hero-band hero-video-band">
      <h1 className="sr-only">{event.headline}. A baby shower for {event.hosts}, {event.dateDisplay}.</h1>
      <div className="hero-video-frame">
        <video
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/assets/invitation.png"
          aria-label={`${event.headline} — a baby shower for ${event.hosts}`}
        >
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        {/* Real RSVP button laid directly over the painted one in the video.
            Self-styled (not .btn) so it scales with the video at every width. */}
        <a href="#rsvp" className="hero-rsvp-overlay" aria-label="RSVP">RSVP</a>
      </div>
    </section>
  );
}

/* ----------------------------- event details ----------------------------- */
function Details({ event }) {
  const hasVenue = event.venueName || event.venueAddress;
  return (
    <section id="details" className="band">
      <div className="wrap">
        <SectionHead eyebrow="The gathering" title="When & where" />
        <div className="detail-grid">
          <div className="card detail-card">
            <div className="detail-icon"><Wildflower size={46} color="#e8b84b" /></div>
            <div className="label" style={{ color: 'var(--sage)' }}>The day</div>
            <p className="detail-big">{event.dateDisplay}</p>
            <p className="muted" style={{ margin: 0 }}>{event.time}{event.endTime ? ` to ${event.endTime}` : ''}</p>
          </div>
          <div className="card detail-card">
            <div className="detail-icon"><LeafSprig size={50} /></div>
            <div className="label" style={{ color: 'var(--sage)' }}>The place</div>
            {hasVenue ? (
              <>
                {event.venueName && <p className="detail-big">{event.venueName}</p>}
                {event.venueAddress && <p className="muted" style={{ margin: 0 }}>{event.venueAddress}</p>}
              </>
            ) : (
              <p className="muted" style={{ margin: 0 }}>The venue will be shared here soon. Check back, or watch for the formal invitation.</p>
            )}
          </div>
        </div>
        <div className="row center" style={{ marginTop: 22 }}>
          <button className="btn btn-secondary" onClick={() => downloadICS(event)}>Add to calendar</button>
          {hasVenue && (
            <a className="btn btn-ghost" href={directionsUrl(event)} target="_blank" rel="noopener noreferrer">Get directions</a>
          )}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- registry ------------------------------- */
function Registry({ entries }) {
  return (
    <section id="registry" className="band gingham-band">
      <div className="wrap">
        <SectionHead eyebrow="Presence over presents" title="Gifts & Registry"
          lead="Your being here is the gift. If you would like to bring something, here are a few gentle ideas." />
        {(!entries || entries.length === 0) ? (
          <div className="card center muted">
            <Ladybug size={40} />
            <p style={{ margin: '10px 0 0' }}>Registry details are on their way. Check back soon.</p>
          </div>
        ) : (
          <div className="registry-grid">
            {entries.map((e) => (
              <div key={e.id} className="card registry-card">
                <h3 style={{ fontSize: '1.6rem', marginBottom: 6 }}>{e.label}</h3>
                {e.type === 'note' ? (
                  <p className="muted" style={{ margin: 0 }}>{e.description}</p>
                ) : (
                  <>
                    {e.description && <p className="muted" style={{ marginBottom: 14 }}>{e.description}</p>}
                    <a className="btn btn-secondary btn-sm" href={normalizeUrl(e.url)} target="_blank" rel="noopener noreferrer">Open registry</a>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------------------------------- RSVP --------------------------------- */
function RSVPSection() {
  return (
    <section id="rsvp" className="band">
      <div className="wrap">
        <SectionHead eyebrow="Will we see you?" title="RSVP"
          lead="Let us know if you can join. Kindly reply so we can set a place for you." />
        <RSVPForm />
      </div>
    </section>
  );
}

/* ------------------------------- well wishes ----------------------------- */
function WishesSection({ showWall }) {
  const [reloadKey, setReloadKey] = useState(0);
  const wall = useAsyncData(() => (showWall ? DB.getWishes() : Promise.resolve([])), [showWall, reloadKey]);
  return (
    <section id="wishes" className="band gingham-band">
      <div className="wrap">
        <SectionHead eyebrow="A note for the parents" title="Well wishes & advice"
          lead="Leave a wish for the baby or a word of wisdom for Kenna and Anthony." />
        <WishForm onSaved={() => setReloadKey((k) => k + 1)} />
        {showWall && (
          <div style={{ marginTop: 30 }}>
            {wall.loading ? <Loading label="Gathering wishes" /> :
             wall.error ? <ErrorBlock error={wall.error} onRetry={wall.reload} /> :
             wall.data.length === 0 ? (
               <p className="center faint">Be the first to leave a wish.</p>
             ) : (
               <div className="wish-wall">
                 {[...wall.data].reverse().map((w) => (
                   <div key={w.id} className="card card-soft wish">
                     <p style={{ margin: '0 0 12px', fontStyle: 'italic', fontSize: '1.05rem' }}>&ldquo;{w.message}&rdquo;</p>
                     <div className="wish-by"><Sprig size={16} /> {w.name}</div>
                   </div>
                 ))}
               </div>
             )}
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------------------- predictions ----------------------------- */
function PredictionsSection() {
  return (
    <section id="predictions" className="band">
      <div className="wrap">
        <SectionHead eyebrow="A little game" title="Baby predictions"
          lead="What is your hunch? Guess the details and we will see who knows this little love bug best." />
        <PredictionForm />
      </div>
    </section>
  );
}

/* -------------------------------- gallery -------------------------------- */
function GallerySection({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section id="gallery" className="band gingham-band">
      <div className="wrap">
        <SectionHead eyebrow="Moments" title="A little gallery" />
        <div className="gallery-grid">
          {items.map((it) => (
            <figure key={it.id} className="gallery-item">
              <img src={it.url} alt={it.caption || 'Shower photo'} loading="lazy" />
              {it.caption && <figcaption>{it.caption}</figcaption>}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- FAQ ---------------------------------- */
function FAQSection({ entries }) {
  const [open, setOpen] = useState(null);
  if (!entries || entries.length === 0) return null;
  return (
    <section id="faq" className="band">
      <div className="wrap" style={{ maxWidth: 660 }}>
        <SectionHead eyebrow="Good to know" title="Questions" />
        <div className="faq-list">
          {entries.map((e, i) => (
            <div key={e.id} className={`faq-item ${open === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpen(open === i ? null : i)} aria-expanded={open === i}>
                <span>{e.q}</span>
                <span className="faq-mark" aria-hidden="true">{open === i ? '–' : '+'}</span>
              </button>
              {open === i && <div className="faq-a">{e.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- schedule ------------------------------- */
function ScheduleSection({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section id="schedule" className="band gingham-band">
      <div className="wrap" style={{ maxWidth: 580 }}>
        <SectionHead eyebrow="The flow of the day" title="Schedule" />
        <div className="card">
          {items.map((s, i) => (
            <div key={s.id || i} className="sched-row">
              <div className="sched-time">{s.time}</div>
              <div className="sched-item">{s.item}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- virtual ------------------------------- */
function VirtualSection({ virtual }) {
  if (!virtual || (!virtual.videoLink && !virtual.gamesPdfLink)) return null;
  return (
    <section id="virtual" className="band">
      <div className="wrap" style={{ maxWidth: 580 }}>
        <SectionHead eyebrow="Joining from afar" title="Celebrate with us, anywhere"
          lead="Cannot be there in person? We would still love to have you." />
        <div className="card center stack">
          {virtual.videoLink && <a className="btn btn-primary" href={normalizeUrl(virtual.videoLink)} target="_blank" rel="noopener noreferrer">Join the video call</a>}
          {virtual.gamesPdfLink && <a className="btn btn-secondary" href={normalizeUrl(virtual.gamesPdfLink)} target="_blank" rel="noopener noreferrer">Printable games</a>}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- footer -------------------------------- */
function GuestFooter({ event }) {
  return (
    <footer className="gfoot">
      <div className="wrap center">
        <div className="row center" style={{ gap: 18, marginBottom: 18 }}>
          <Ladybug size={30} /><Sprig size={22} /><Bee size={34} />
        </div>
        <p className="foot-note">{event.closingNote}</p>
        <p className="serif" style={{ fontSize: '1.6rem', color: '#f1ead9', marginTop: 14 }}>{event.hosts}</p>
        <details className="foot-privacy">
          <summary>Your privacy</summary>
          <p>
            The name, email, and address you share are used only to plan this shower and to mail
            your invitation and a thank-you card. They are never shared beyond {event.hosts}, and
            there are no third-party trackers on this page. To have your details removed, just let
            the hosts know.
          </p>
          <p>
            This page uses Cloudflare Turnstile to quietly keep out spam and bots. Its use is
            covered by Cloudflare&apos;s{' '}
            <a
              href="https://www.cloudflare.com/turnstile-privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Turnstile Privacy Addendum
            </a>.
          </p>
        </details>
      </div>
    </footer>
  );
}

/* ------------------------------- guest page ------------------------------ */
export function GuestPage() {
  const { loading, error, data, reload } = useAsyncData(() => DB.getSettings(), []);
  const gallery = useAsyncData(() => DB.getGalleryItems(), []);

  if (loading) return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><Loading label="Setting the table" /></div>;
  if (error) return <div className="wrap" style={{ paddingTop: 80 }}><ErrorBlock error={error} onRetry={reload} /></div>;

  const { event, toggles, registry, faq, virtual, schedule } = data;
  const galleryItems = gallery.data || [];

  return (
    <div className="page">
      <GuestNav toggles={toggles} hosts={event.hosts} />
      <Hero event={event} />
      <Details event={event} />
      {toggles.registry && <Registry entries={registry} />}
      <RSVPSection />
      {toggles.wishes && <WishesSection showWall={toggles.wishesPublic} />}
      {toggles.predictions && <PredictionsSection />}
      {toggles.gallery && <GallerySection items={galleryItems} />}
      {toggles.faq && <FAQSection entries={faq} />}
      {toggles.schedule && <ScheduleSection items={schedule} />}
      {toggles.virtual && <VirtualSection virtual={virtual} />}
      <GuestFooter event={event} />
    </div>
  );
}
